class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    
    // Fixed: Add proper delay to prevent immediate closing
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    
    // NEW: Click outside to close
    document.addEventListener('click', this.onClickOutside.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut(event) {
    // Fixed: Add 200ms delay to allow clicks on results
    setTimeout(() => {
      const newFocusedElement = document.activeElement;
      const isInsideComponent = this.contains(newFocusedElement);
      const isHoveringResults = this.matches(':hover');
      
      if (!isInsideComponent && !isHoveringResults) {
        this.close();
      }
    }, 200);
  }

  onClickOutside(event) {
    // Close if clicking outside the search component
    if (!this.contains(event.target) && this.isOpen) {
      this.close();
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // FIXED: Use Shopify's standard predictive search endpoint
    // This fetches all available resource types automatically
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        
        // Check if we got valid results
        if (resultsMarkup && resultsMarkup.trim().length > 0) {
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
        } else {
          console.error('PredictiveSearch: Empty results received');
          this.close();
        }
      })
      .catch((error) => {
        console.error('PredictiveSearch: Fetch error', error);
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegionElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionElement) {
      this.setLiveRegionText(liveRegionElement.textContent);
    }
  }

  getResultsMaxHeight() {
    const header = document.getElementById('shopify-section-header');
    if (header) {
      this.resultsMaxHeight = window.innerHeight - header.getBoundingClientRect().bottom;
    } else {
      this.resultsMaxHeight = window.innerHeight - 100; // Fallback
    }
    return this.resultsMaxHeight;
  }

  open() {
    
    // Calculate header height for overlay positioning
    const header = document.getElementById('shopify-section-header') || document.querySelector('.header');
    if (header) {
      const headerHeight = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      
      // Set header bottom position for mobile fixed dropdown
      const updateHeaderBottom = () => {
        const rect = header.getBoundingClientRect();
        const bottom = Math.max(0, rect.bottom);
        document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
      };
      updateHeaderBottom();
      
      // Store reference so we can remove the listener on close
      this._updateHeaderBottom = updateHeaderBottom;
      window.addEventListener('scroll', updateHeaderBottom, { passive: true });
      window.addEventListener('resize', updateHeaderBottom, { passive: true });
    }
    
    // Force visibility
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    
    // Set attributes that trigger CSS visibility
    this.setAttribute('open', true);
    this.setAttribute('results', 'true');
    this.input.setAttribute('aria-expanded', true);
    
    // Add body class for overlay
    document.body.classList.add('predictive-search-open');
    
    // Handle modal-overlay if it exists
    const modalOverlay = document.querySelector('.modal-overlay');
    if (modalOverlay) {
      modalOverlay.classList.add('active');
    }
    
    // Force display block on the results container
    const predictiveSearchElement = this.querySelector('.predictive-search');
    if (predictiveSearchElement) {
      predictiveSearchElement.style.display = 'block';
    } else {
      console.error('PredictiveSearch: .predictive-search element not found');
    }
    
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    
    // Remove scroll listener for header bottom tracking
    if (this._updateHeaderBottom) {
      window.removeEventListener('scroll', this._updateHeaderBottom);
      window.removeEventListener('resize', this._updateHeaderBottom);
      this._updateHeaderBottom = null;
    }
    
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
    
    // Remove body class for overlay
    document.body.classList.remove('predictive-search-open');
    
    // Remove active class from modal-overlay
    const modalOverlay = document.querySelector('.modal-overlay');
    if (modalOverlay) {
      modalOverlay.classList.remove('active');
    }

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
